import { GlobalStore } from '@highlight/ipc'
import { uuidV1Key, uuidV2Key } from '@shared/config/Config.ts'

export const getDeviceUUID = (version?: 'v1' | 'v2') => {
  return GlobalStore.device().get<string>(version !== 'v1' ? uuidV2Key : uuidV1Key)
}

export const setDeviceUUID = (uuid: string, version?: 'v1' | 'v2') => {
  GlobalStore.device().set<string>(version !== 'v1' ? uuidV2Key : uuidV1Key, uuid)
}

/** Names this install to the backend, so it can send tool calls to this computer when several are signed in. */
export const deviceIdHeader = (): Record<string, string> => {
  const deviceId = getDeviceUUID()
  return deviceId ? { 'x-device-id': deviceId } : {}
}

export const getAmplitudeDeviceId = () => {
  return GlobalStore.device().get<string>('amplitudeDeviceId')
}

export const setAmplitudeDeviceId = (deviceId: string | null) => {
  GlobalStore.device().set<string | null>('amplitudeDeviceId', deviceId)
}

/**
 * The PostHog distinct id for this install, while signed out.
 *
 * Its own value rather than a read of the Amplitude one: each provider mints
 * and owns its ids, so retiring Amplitude must not renumber anyone in PostHog.
 * Set once from web attribution when the visitor who downloaded can be
 * identified, otherwise from the device UUID, and then never changed, since
 * changing it splits one person in two.
 */
export const getPostHogDeviceId = () => {
  return GlobalStore.device().get<string>('posthogDeviceId')
}

export const setPostHogDeviceId = (distinctId: string) => {
  GlobalStore.device().set<string>('posthogDeviceId', distinctId)
}

/**
 * Whether this install has already claimed its web attribution.
 *
 * Its own flag rather than an inference from either provider's id. The store
 * coalesces a stored `null` to `undefined`, so "Amplitude has no id" cannot
 * tell a fresh install from one whose attribution lookup already came back
 * empty, and the download worker deletes the entry on the first read either
 * way. Inferring it re-ran the lookup on every launch.
 */
/**
 * Whether this install already ran the attribution step before the claim flag
 * existed.
 *
 * The step always writes `amplitudeDeviceId`, `null` included, so the key
 * being present is the record that it ran. `get` cannot answer this: it
 * coalesces a stored `null` to `undefined`, which is also what an absent key
 * reads as.
 */
export const hasResolvedAttributionBefore = () => {
  return 'amplitudeDeviceId' in GlobalStore.device().all()
}

export const getInstallAttributionClaimed = () => {
  return GlobalStore.device().get<boolean>('installAttributionClaimed') === true
}

export const setInstallAttributionClaimed = () => {
  GlobalStore.device().set<boolean>('installAttributionClaimed', true)
}
